import { ChangeEvent } from 'react'

import { Input, Select } from 'antd'
import { Rule } from 'antd/lib/form'

import { WeatherCaptureBlock as WeatherCaptureBlockType } from '../../types/blueprint'
import {
  WeatherInput,
  WeatherSelect,
} from '../CustomBlocks/Weather/Weather.style'
import { weatherValues } from '../CustomBlocks/Weather/weatherConstants'

import { CaptureBlockFormItem } from './CaptureBlockFormItem'

import { TemperatureUnit, useLocale } from '@/context/localeContext'
import { useProject } from '@/context/projectContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useWeatherLatLng } from '@/hooks/useWeather'

type Value = WeatherCaptureBlockType['value']
type WeatherTypes = WeatherCaptureBlockType['value']['weather']

type CaptureProps = {
  value?: Value
  onChange?: (value: Value) => void
  temperatureUnit?: TemperatureUnit
}

const CaptureWeather: React.FC<CaptureProps> = ({
  value = {} as Value,
  onChange,
}) => {
  const { temperatureUnit } = useLocale()
  const smallDevice = useBreakpoint('md')

  const onWeatherChange = (weather: WeatherTypes) => {
    onChange({ ...value, weather })
  }

  const onTempChange = (e: ChangeEvent<HTMLInputElement>) => {
    const temperature = parseFloat(e.target.value)
    if (isNaN(temperature)) return
    onChange({ ...value, temperature })
  }

  return (
    <Input.Group compact>
      <WeatherSelect
        value={value.weather}
        size={smallDevice ? 'large' : 'middle'}
        onChange={onWeatherChange}
        placeholder="Weather"
      >
        {weatherValues.map(({ value, icon, label }) => (
          <Select.Option key={value} value={value}>
            <span role="img" aria-label={label}>
              {icon}
            </span>
            &nbsp; {label}
          </Select.Option>
        ))}
      </WeatherSelect>
      <WeatherInput
        size={smallDevice ? 'large' : 'middle'}
        type="number"
        placeholder="24"
        addonAfter={temperatureUnit}
        onInput={onTempChange}
        value={value.temperature}
      />
    </Input.Group>
  )
}

type Props = {
  block: WeatherCaptureBlockType
  rules: Rule[]
}

export const WeatherCaptureBlock: React.FC<Props> = ({ block, rules }) => {
  const { value } = block
  const { project } = useProject() || {}
  const cancelFetch =
    !!value?.weather ||
    (!value?.weather && (!project?.latitude || !project?.longitude))
  const [weather, loadingWeather] = useWeatherLatLng(
    cancelFetch,
    project?.latitude,
    project?.longitude
  )

  const initialWeather: Value | Record<string, never> = value?.weather
    ? {
        weather: value.weather,
        temperature: value?.temperature,
      }
    : weather
      ? {
          weather: weather.simplifiedWeather,
          temperature: Math.round(weather.temperatureMax),
        }
      : {}

  return loadingWeather ? null : (
    <CaptureBlockFormItem
      block={block}
      initialValue={initialWeather}
      rules={rules}
    >
      <CaptureWeather />
    </CaptureBlockFormItem>
  )
}
